'use client';
import {
  experimentInstance,
  startExperiment,
} from '@payments-checkout/shared/plugins/analitica/analitica.configs';
import {
  usePaymentMethod,
  useSessionId,
} from '@payments-checkout/shared/providers';
import { useParams } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { usePaymentLinkId } from '../usePaymentLinkId';

const EXPERIMENT_KEY = 'checkout-desabilitar-selecao-automatica-de-metodo-de-pagamento';
const VARIANT_TIMEOUT_MS = 1500;
const PENDING_FALLBACK_MS = 2000;

function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number
): Promise<T | undefined> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(undefined), timeoutMs);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      () => {
        clearTimeout(timer);
        resolve(undefined);
      }
    );
  });
}

export function useLoadPreselectExperiment() {
  const params = useParams<{ paymentLinkId: string }>();
  const { data } = usePaymentLinkId(params?.paymentLinkId);
  const { sessionId } = useSessionId();
  const { setPreselectExperiment } = usePaymentMethod();
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;

    const merchantId = data?.account_settings?.merchant_id;
    const amplitudeIdentifier = sessionStorage.getItem('amplitudeIdentifier');
    if (!merchantId || !amplitudeIdentifier) return;

    startedRef.current = true;

    (async () => {
      try {
        await startExperiment({
          userId: amplitudeIdentifier,
          accountId: data?.account_settings?.account_id,
          merchantId,
        });

        const variant = await withTimeout(
          experimentInstance.variant(EXPERIMENT_KEY),
          VARIANT_TIMEOUT_MS
        );

        setPreselectExperiment(variant?.key === 'treatment' ? 'treatment' : 'control');
      } catch {
        setPreselectExperiment('control');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, data?.account_settings?.merchant_id, setPreselectExperiment]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPreselectExperiment((current) =>
        current === 'pending' ? 'control' : current
      );
    }, PENDING_FALLBACK_MS);

    return () => clearTimeout(timer);
  }, [setPreselectExperiment]);
}
